import React, { useState } from 'react';
import { 
  Camera, Upload, AlertTriangle, CheckCircle,
  XCircle, Eye, Download, RefreshCw, Scan
} from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';

interface AnalysisResult {
  complianceScore: number;
  detectedIssues: {
    type: 'stockout' | 'misplaced' | 'damaged' | 'incorrect_facing';
    severity: 'critical' | 'warning' | 'info';
    location: string;
    description: string;
    image?: string;
  }[];
  matchedProducts: number;
  totalProducts: number;
  timestamp: Date;
}

interface ShelfImageAnalyzerProps {
  store: string;
}

const ShelfImageAnalyzer: React.FC<ShelfImageAnalyzerProps> = ({ store }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [viewMode, setViewMode] = useState<'original' | 'annotated'>('original');
  
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleAnalyze = () => {
    setIsAnalyzing(true);
    setTimeout(() => {
      setAnalysisResult({
        complianceScore: 82,
        detectedIssues: [
          {
            type: 'stockout',
            severity: 'critical',
            location: '棚2-列3',
            description: 'コカ・コーラ500mlが欠品しています'
          },
          {
            type: 'misplaced',
            severity: 'warning',
            location: '棚3-列5',
            description: 'ポテトチップスが指定位置と異なります'
          },
          {
            type: 'incorrect_facing',
            severity: 'info',
            location: '棚4-列2',
            description: 'フェース数が計画と異なります（計画3→実際2）'
          },
          {
            type: 'damaged',
            severity: 'warning',
            location: '棚5-列7',
            description: 'プライスカードが破損しています'
          }
        ],
        matchedProducts: 142,
        totalProducts: 173,
        timestamp: new Date()
      });
      setIsAnalyzing(false);
    }, 3000);
  };
  
  const getIssueIcon = (type: string) => {
    switch (type) {
      case 'stockout': return <XCircle size={16} style={{ color: colors.status.error }} />;
      case 'misplaced': return <AlertTriangle size={16} style={{ color: colors.status.warning }} />;
      case 'damaged': return <AlertTriangle size={16} style={{ color: colors.status.warning }} />;
      case 'incorrect_facing': return <Eye size={16} style={{ color: colors.status.info }} />;
      default: return <AlertTriangle size={16} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* アップロードエリア */}
      <Card className="p-6">
        <div className="text-center">
          <Camera size={48} style={{ color: colors.text.secondary, margin: '0 auto' }} />
          <h3 className="mt-3 font-semibold" style={{ color: colors.text.primary }}>
            棚画像をアップロード
          </h3>
          <p className="mt-1 text-sm" style={{ color: colors.text.secondary }}>
            JPG、PNG形式（最大10MB）
          </p>
          
          <div className="mt-4">
            <label className="cursor-pointer">
              <input 
                type="file" 
                className="hidden" 
                accept="image/*"
                onChange={handleImageUpload}
              />
              <Button variant="primary">
                <Upload size={16} />
                画像を選択
              </Button>
            </label>
          </div>
          
          {uploadedImage && (
            <div className="mt-4 flex justify-center gap-2">
              <Button 
                variant="primary" 
                onClick={handleAnalyze}
                disabled={isAnalyzing}
              >
                {isAnalyzing ? (
                  <>
                    <Scan size={16} className="animate-pulse" />
                    解析中...
                  </>
                ) : (
                  <>
                    <Scan size={16} />
                    画像解析開始
                  </>
                )}
              </Button>
              <Button 
                variant="secondary"
                onClick={() => {
                  setUploadedImage(null);
                  setAnalysisResult(null);
                }}
              >
                <RefreshCw size={16} />
                リセット
              </Button>
            </div>
          )}
        </div>
      </Card>
      
      {/* 画像表示エリア */}
      {uploadedImage && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold" style={{ color: colors.text.primary }}>
              アップロード画像
            </h3>
            {analysisResult && (
              <div className="flex gap-2">
                <Button
                  variant={viewMode === 'original' ? 'primary' : 'secondary'}
                  size="small"
                  onClick={() => setViewMode('original')}
                >
                  オリジナル
                </Button>
                <Button
                  variant={viewMode === 'annotated' ? 'primary' : 'secondary'}
                  size="small"
                  onClick={() => setViewMode('annotated')}
                >
                  解析結果
                </Button>
              </div>
            )}
          </div>
          
          <div className="relative">
            <img 
              src={uploadedImage} 
              alt="Shelf" 
              className="w-full rounded-lg"
            />
            
            {/* 解析結果のオーバーレイ（モック） */}
            {viewMode === 'annotated' && analysisResult && (
              <>
                <div className="absolute top-10 left-20 w-16 h-16 border-2 border-red-500 rounded animate-pulse">
                  <div className="absolute -top-6 left-0 bg-red-500 text-white text-xs px-2 py-1 rounded">
                    欠品
                  </div>
                </div>
                
                <div className="absolute top-32 right-40 w-20 h-20 border-2 border-yellow-500 rounded">
                  <div className="absolute -top-6 left-0 bg-yellow-500 text-white text-xs px-2 py-1 rounded">
                    配置違い
                  </div>
                </div>
                
                <div className="absolute bottom-20 left-32 w-24 h-16 border-2 border-blue-500 rounded">
                  <div className="absolute -bottom-6 left-0 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                    フェース違い
                  </div>
                </div>
              </>
            )}
          </div>
        </Card>
      )}
      
      {/* 解析結果 */}
      {analysisResult && (
        <>
          {/* スコアカード */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <svg className="w-12 h-12 transform -rotate-90">
                    <circle
                      cx="24"
                      cy="24"
                      r="20"
                      stroke={colors.ui.border}
                      strokeWidth="4"
                      fill="none"
                    />
                    <circle
                      cx="24"
                      cy="24"
                      r="20"
                      stroke={colors.support.success}
                      strokeWidth="4"
                      fill="none"
                      strokeDasharray={`${analysisResult.complianceScore * 1.26} 126`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-bold">{analysisResult.complianceScore}%</span>
                  </div>
                </div>
                <div>
                  <div className="text-xs" style={{ color: colors.text.secondary }}>
                    遵守率
                  </div>
                </div>
              </div>
            </Card>
            
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle size={20} style={{ color: colors.support.success }} />
                <div>
                  <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                    {analysisResult.matchedProducts}
                  </div>
                  <div className="text-xs" style={{ color: colors.text.secondary }}>
                    正常配置
                  </div>
                </div>
              </div>
            </Card>
            
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle size={20} style={{ color: colors.status.warning }} />
                <div>
                  <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                    {analysisResult.detectedIssues.length}
                  </div>
                  <div className="text-xs" style={{ color: colors.text.secondary }}>
                    検出問題
                  </div>
                </div>
              </div>
            </Card>
            
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <XCircle size={20} style={{ color: colors.status.error }} />
                <div>
                  <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                    {analysisResult.detectedIssues.filter(i => i.type === 'stockout').length}
                  </div>
                  <div className="text-xs" style={{ color: colors.text.secondary }}>
                    欠品箇所
                  </div>
                </div>
              </div>
            </Card>
          </div>
          
          {/* 問題リスト */}
          <Card>
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: colors.ui.border }}>
              <h3 className="font-semibold" style={{ color: colors.text.primary }}>
                検出された問題
              </h3>
              <Button variant="secondary" size="small">
                <Download size={16} />
                レポート出力
              </Button>
            </div>
            
            <div className="divide-y" style={{ borderColor: colors.ui.border }}>
              {analysisResult.detectedIssues.map((issue, idx) => (
                <div key={idx} className="p-4 flex items-start gap-4">
                  <div className="mt-1">
                    {getIssueIcon(issue.type)}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium" style={{ color: colors.text.primary }}>
                        {issue.location}
                      </span>
                      <Badge 
                        variant={issue.severity === 'critical' ? 'error' : 
                                issue.severity === 'warning' ? 'warning' : 'secondary'}
                        size="small"
                      >
                        {issue.severity === 'critical' ? '重要' : 
                         issue.severity === 'warning' ? '警告' : '情報'}
                      </Badge>
                    </div>
                    <p className="text-sm" style={{ color: colors.text.secondary }}>
                      {issue.description}
                    </p>
                  </div>
                  
                  <Button variant="secondary" size="small">
                    対応指示
                  </Button>
                </div>
              ))}
            </div>
          </Card>
          
          {/* 改善提案 */}
          <Card className="p-4 border-l-4" style={{ borderLeftColor: colors.accent.blue }}>
            <div className="flex items-start gap-3">
              <Eye size={20} style={{ color: colors.accent.blue }} />
              <div className="flex-1">
                <h4 className="font-semibold mb-2" style={{ color: colors.text.primary }}>
                  改善提案
                </h4>
                <ul className="space-y-2 text-sm" style={{ color: colors.text.secondary }}>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>欠品商品の即時補充により、売上機会損失を防げます（推定+¥15,000/日）</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>配置違いを修正することで、顧客の商品発見率が向上します</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>フェース数を計画通りに調整することで、在庫効率が改善されます</span>
                  </li>
                </ul>
                
                <div className="mt-3 flex gap-2">
                  <Button variant="primary" size="small">
                    改善指示書を作成
                  </Button>
                  <Button variant="secondary" size="small">
                    店舗に通知
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default ShelfImageAnalyzer;
